/**
 * Native inline-checkout island entry (no iframe).
 *
 * Bundled standalone by `scripts/build-checkout-island.ts` (esbuild) into
 * /public/mdb-checkout-island.js and mounted into the plain HTML landing page at
 * `#mdb-ci-root`. The LP route injects the script + CSS + relies on the existing
 * `window.__CHECKOUT_CONFIG__` (only for LPs flagged into the native variant).
 *
 * LAZY MOUNT: the island mounts only when the checkout scrolls near the viewport
 * (or when an LP plan button forces it via window.__mdbCiMount) — so the bundle
 * + Stripe load + the PaymentIntent/order are created only for buyers who reach
 * the form, not on every page load.
 */
import { createRoot } from "react-dom/client";
import { CheckoutInline } from "./CheckoutInline";
import {
  installmentsEligibility,
  type InstallmentsEligibility,
} from "./installments-eligibility";
import {
  mountInstallmentsMessaging,
  type InstallmentsMessagingOptions,
} from "./installments-messaging-mount";
import { runRecoveryDeepLink, type RecoveryPrefill } from "./recovery-deeplink";
import type { IslandConfig } from "./use-checkout-inline";

declare global {
  interface Window {
    __CHECKOUT_CONFIG__?: IslandConfig & Record<string, unknown>;
    MDBCheckout?: {
      mount: () => void;
      /** opts.installments puts Klarna/Afterpay first in the Payment Element. */
      selectPlan?: (pid: string, opts?: { installments?: boolean }) => void;
      /** Decides whether the LP's injected installments block may be shown at
       *  all: a FRESH geo lookup, once per page, shared by every card. This -
       *  not the provider element - is what reveals the block. */
      installmentsEligibility?: () => Promise<InstallmentsEligibility>;
      /** Mounts Stripe's PaymentMethodMessagingElement into `el` for the LP's
       *  injected installments block. Best-effort decoration of an ALREADY
       *  revealed block; `rendered` is reported for logging only. */
      mountInstallmentsMessaging?: (
        el: HTMLElement,
        opts: InstallmentsMessagingOptions,
      ) => Promise<{ rendered: boolean }>;
      /** Applies a recovery-link prefill to the live form (email overrides a
       *  stored one; names/country fill gaps; typed values always survive). */
      applyPrefill?: (p: RecoveryPrefill) => void;
    };
    __mdbCiMount?: () => void;
    /** Plan buffered before the island mounted. A bare id is the long-standing
     *  shape written by the emitted LP glue (mdbEmbedSelect); the object form
     *  carries the installments option for callers that have it. */
    __mdbCiPendingPlan?: string | { pid: string; installments?: boolean };
    /** Installments intent set by the LP's injected secondary control right
     *  before the page's own handleCheckout() runs. Holds the product id (or
     *  true), and the island clears it on the next selection. It exists so the
     *  emitted LP glue stays byte-identical and add_to_cart fires exactly once. */
    __mdbCiInstallments?: string | boolean;
  }
}

let mounted = false;
function mount() {
  if (mounted) return;
  const el = document.getElementById("mdb-ci-root");
  if (!el) return;
  mounted = true;
  const config = (window.__CHECKOUT_CONFIG__ || {}) as IslandConfig;
  createRoot(el).render(<CheckoutInline config={config} />);
}

// Force-mount hook for the LP plan buttons (mdbEmbedSelect → window.__mdbCiMount).
window.__mdbCiMount = mount;
// installmentsEligibility + mountInstallmentsMessaging are exposed
// UNCONDITIONALLY and independently of the React mount: on a scroll_direct
// landing page there is no #mdb-ci-root at all, the bundle is loaded purely so
// the injected installments block can ask whether it may show itself (geo) and
// then, best-effort, ask Stripe for its messaging line.
window.MDBCheckout = {
  ...(window.MDBCheckout || {}),
  mount,
  installmentsEligibility,
  mountInstallmentsMessaging,
};

function init() {
  const el = document.getElementById("mdb-ci-root");
  if (!el) return;

  // Recovery deep link (?rc=…): resolve, prefill, preselect the plan, mount and
  // jump to the checkout. Self-guards on the param and on this root element, so
  // an ordinary visit does no work and makes no request.
  void runRecoveryDeepLink(
    (window.__CHECKOUT_CONFIG__ || {}) as IslandConfig,
  ).catch(() => {});

  // Mount as soon as the buyer scrolls past the first screen — preload the
  // checkout (bundle + Stripe + PaymentIntent) so it's ready by the time they
  // reach it — OR when it nears the viewport, whichever fires first. A bare
  // page-load with no scroll never mounts (no PI for pure bouncers).
  let io: IntersectionObserver | null = null;
  let armed = true;
  const cleanup = () => {
    window.removeEventListener("scroll", onScroll);
    if (io) io.disconnect();
  };
  const fire = () => {
    if (!armed) return;
    armed = false;
    cleanup();
    mount();
  };
  function onScroll() {
    if (window.scrollY > window.innerHeight * 0.8) fire();
  }

  if ("IntersectionObserver" in window) {
    io = new IntersectionObserver(
      (entries) => {
        for (const e of entries) {
          if (e.isIntersecting) {
            fire();
            break;
          }
        }
      },
      { rootMargin: "600px 0px" },
    );
    io.observe(el);
  }
  window.addEventListener("scroll", onScroll, { passive: true });
  onScroll(); // already scrolled past the fold on load (e.g. anchor / restore)
}

if (document.readyState === "loading") {
  document.addEventListener("DOMContentLoaded", init);
} else {
  init();
}
